import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MdAdd, MdCampaign, MdClose, MdDelete, MdEdit } from 'react-icons/md';
import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import announcementServices from 'ApiService/Common/announcementServices';
import AnnouncementForm from './AnnouncementForm';
import ConfirmDialog from './ConfirmDialog';
import {
  announcementsPathFor,
  AudienceChip,
  formatAnnouncementDate,
  postedByLabel,
} from './helpers';

// Superadmin sees every branch and batch, so "mine" would be misleading there.
const tabsForRole = (role) => {
  const all = { key: 'all', label: 'All' };
  if (role === 'superadmin') {
    return [all, { key: 'branch', label: 'Branch' }, { key: 'batch', label: 'Batch' }];
  }
  return [
    all,
    { key: 'branch', label: 'My Branch' },
    { key: 'batch', label: role === 'student' ? 'My Batch' : 'My Batches' },
  ];
};

const AnnouncementsCard = () => {
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();
  const tabs = useMemo(() => tabsForRole(state?.user?.role), [state?.user?.role]);
  // Null for students and accountants, who have no Announcements page.
  const createPath = announcementsPathFor(state?.user);
  const [activeTab, setActiveTab] = useState('all');
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [targets, setTargets] = useState({
    scopes: [],
    branches: [],
    batches: [],
  });

  const fetchAnnouncements = useCallback(async (tab) => {
    setLoading(true);
    try {
      // The "All" tab shows everything visible to the viewer (global + branch +
      // batch); the other tabs ask the API for that one scope.
      const params = { limit: 20, ...(tab !== 'all' && { scope: tab }) };
      const response = await announcementServices.list(params);
      if (response.data.success) {
        setAnnouncements(response.data.data);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch announcements'
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAnnouncements(activeTab);
  }, [activeTab, fetchAnnouncements]);

  // Escape closes the editor, like clicking outside it.
  useEffect(() => {
    if (!editing) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setEditing(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editing]);

  // Loaded lazily: a student never opens the editor, so never asks for targets.
  const handleEdit = async (announcement) => {
    setEditing(announcement);
    if (targets.scopes.length) return;
    try {
      const response = await announcementServices.getTargets();
      if (response.data.success) {
        setTargets(response.data.data);
      }
    } catch {
      // The "Keep current" audience still works without the target list.
    }
  };

  const handleSaved = (saved) => {
    setAnnouncements((prev) =>
      prev.map((item) => (item._id === saved._id ? saved : item))
    );
    setEditing(null);
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      const response = await announcementServices.remove(id);
      if (response.data.success) {
        toast.success(response.data.message);
        setAnnouncements((prev) => prev.filter((item) => item._id !== id));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to delete announcement'
      );
    }
    setDeleting(false);
    setConfirmingDelete(null);
  };

  const TabButton = ({ tab, label }) => (
    <button
      type="button"
      onClick={() => setActiveTab(tab)}
      className={`px-4 py-2 text-sm ${
        activeTab === tab
          ? 'border-b-2 border-blue-500 text-blue-600'
          : 'text-gray-500'
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
      <Card extra="w-full p-6 mb-5">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MdCampaign className="h-6 w-6 text-blue-500" />
            <h4 className="text-lg font-semibold text-navy-700 dark:text-white">
              Announcements
            </h4>
          </div>
          {createPath && (
            <button
              type="button"
              onClick={() => navigate(createPath)}
              className="flex items-center gap-1 rounded-xl bg-blue-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-600"
            >
              <MdAdd className="h-4 w-4" />
              Create Announcement
            </button>
          )}
        </div>

        <div className="mb-4 flex border-b border-gray-200 dark:border-white/10">
          {tabs.map((tab) => (
            <TabButton key={tab.key} tab={tab.key} label={tab.label} />
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
          </div>
        ) : announcements.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            No announcements here yet.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {announcements.map((announcement) => (
              <div
                key={announcement._id}
                className="rounded-xl border border-gray-200 p-4 dark:border-white/10"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h5 className="text-base font-semibold text-navy-700 dark:text-white">
                      {announcement.title}
                    </h5>
                    <p className="mt-1 whitespace-pre-line text-sm text-gray-600 dark:text-gray-300">
                      {announcement.message}
                    </p>
                  </div>
                  {/* canEdit / canDelete come from the API, so the rule lives in one place. */}
                  <div className="flex shrink-0 items-center gap-1">
                    {announcement.canEdit && (
                      <button
                        type="button"
                        onClick={() => handleEdit(announcement)}
                        title="Edit announcement"
                        className="rounded-lg p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-navy-700"
                      >
                        <MdEdit className="h-5 w-5" />
                      </button>
                    )}
                    {announcement.canDelete && (
                      <button
                        type="button"
                        onClick={() => setConfirmingDelete(announcement)}
                        title="Delete announcement"
                        className="rounded-lg p-2 text-red-500 hover:bg-red-50 dark:hover:bg-navy-700"
                      >
                        <MdDelete className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <AudienceChip announcement={announcement} />
                  <span>{postedByLabel(announcement)}</span>
                  <span>{formatAnnouncementDate(announcement.createdAt)}</span>
                  {announcement.updatedAt &&
                    announcement.updatedAt !== announcement.createdAt && (
                      <span className="italic">
                        edited {formatAnnouncementDate(announcement.updatedAt)}
                      </span>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editing && (
        <div
          onClick={() => setEditing(null)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
        >
          {/* Clicks inside the panel must not bubble up to the closing overlay. */}
          <div
            onClick={(event) => event.stopPropagation()}
            className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-[20px] bg-white p-6 shadow-3xl dark:!bg-navy-800 dark:text-white"
          >
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-lg font-semibold text-navy-700 dark:text-white">
                Edit Announcement
              </h4>
              <button
                type="button"
                onClick={() => setEditing(null)}
                title="Close"
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>
            <AnnouncementForm
              editing={editing}
              targets={targets}
              onSaved={handleSaved}
              onCancel={() => setEditing(null)}
            />
          </div>
        </div>
      )}
      {confirmingDelete && (
        <ConfirmDialog
          title="Delete this announcement?"
          message={`"${confirmingDelete.title}" will no longer be visible to anyone. This cannot be undone.`}
          busy={deleting}
          onConfirm={() => handleDelete(confirmingDelete._id)}
          onCancel={() => setConfirmingDelete(null)}
        />
      )}
    </>
  );
};

export default AnnouncementsCard;
